import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { getMergedUserData } from "../pages/const";
import {
  postCancelOrder,
  isErpApiSuccess,
  generateIdempotencyKey,
  getErpRequestErrorMessage,
} from "../erpClient";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

import Select from "react-select";

const AUTO_TYPE_PAY_MODES = ["cash", "wallet", "kasar", "old_gold", "old_silver"];

const findBankAccountForPayMode = (payMode, accounts = []) => {
  if (!payMode) return null;
  const keyword = String(payMode).toLowerCase().replace(/_/g, " ");
  return (
    accounts.find((bank) => {
      const accountType = String(
        bank.account_type || bank.accountType || "",
      ).toLowerCase();
      const bankName = String(bank.bank_name || bank.bankName || "").toLowerCase();
      return (
        accountType === keyword ||
        accountType === String(payMode).toLowerCase() ||
        bankName.includes(keyword)
      );
    }) || null
  );
};

const resolveBankAccountId = (typeValue) => {
  if (typeValue === null || typeValue === undefined || typeValue === "") {
    return null;
  }
  const parsed = parseInt(String(typeValue), 10);
  if (
    !Number.isNaN(parsed) &&
    parsed > 0 &&
    String(parsed) === String(typeValue).trim()
  ) {
    return parsed;
  }
  return null;
};

const resolveRowBankAccountId = (row, accounts = []) => {
  const fromType = resolveBankAccountId(row?.type);
  if (fromType) return fromType;
  const account = findBankAccountForPayMode(row?.pay_mode, accounts);
  return account?.id != null ? Number(account.id) : null;
};

// Payment method options
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "Wallet", label: "Wallet" },
  { value: "old_gold", label: "Old Gold" },
  { value: "old_silver", label: "Old Silver" },
  { value: "kasar", label: "Kasar" },
  { value: "scheme", label: "Scheme" },
  { value: "order", label: "Order" },
  { value: "os", label: "OS" },
];

function CancelOrderModal({
  show,
  onHide,
  orderData,
  selectedItems = [],
  isWholeOrder = false,
  orderType = "custom", // "custom" or "repairing"
  bankAccountsList = [], // Add bank accounts list prop
  onSuccess,
}) {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [cancelStep, setCancelStep] = useState(1);
  const [cancelReason, setCancelReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelDate, setCancelDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [cancelPaymentRows, setCancelPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 2,
      pay_mode: "cheque",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 3,
      pay_mode: "bank_transfer",
      type: "",
      remarks: "",
      amount: "0.00",
    },
  ]);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const userData = getMergedUserData();

  // Listen for API URL changes and close modal
  React.useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in CancelOrderModal, closing modal...', event.detail);
      if (show) {
        toast('Database switched. Closing modal.');
        onHide();
      }
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [show, onHide]);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (show && orderData) {
      setCancelStep(1);
      setCancelReason("");
      setCancelDate(new Date().toISOString().split("T")[0]);
      setCancelRemarks("");
      setCancelPaymentRows([
        {
          id: 1,
          pay_mode: "cash",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 2,
          pay_mode: "cheque",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 3,
          pay_mode: "bank_transfer",
          type: "",
          remarks: "",
          amount: "0.00",
        },
      ]);
    }
  }, [show, orderData]);

  React.useEffect(() => {
    if (!show || !bankAccountsList?.length) return;

    setCancelPaymentRows((prevRows) => {
      let changed = false;
      const nextRows = prevRows.map((row) => {
        if (row.type) return row;
        if (!AUTO_TYPE_PAY_MODES.includes(row.pay_mode)) return row;
        const account = findBankAccountForPayMode(row.pay_mode, bankAccountsList);
        if (!account?.id) return row;
        changed = true;
        return { ...row, type: String(account.id) };
      });
      return changed ? nextRows : prevRows;
    });
  }, [show, bankAccountsList]);

  // Handle payment row changes
  const handleCancelPaymentRowChange = (rowId, field, value) => {
    setCancelPaymentRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;

        const updatedRow = { ...row, [field]: value };
        if (field === "pay_mode" && AUTO_TYPE_PAY_MODES.includes(value)) {
          const account = findBankAccountForPayMode(value, bankAccountsList);
          updatedRow.type = account?.id != null ? String(account.id) : "";
        }
        return updatedRow;
      }),
    );
  };

  // Calculate payment total
  const cancelPaymentTotal = cancelPaymentRows.reduce((sum, row) => {
    return sum + parseFloat(row.amount || 0);
  }, 0);

  const resolveByUser = () =>
    userData?.id ?? userData?.ua_id ?? userData?.user_id ?? null;

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    const byUser = resolveByUser();
    if (!byUser) {
      toast.error("User not logged in");
      return;
    }

    setIsSubmitting(true);
    try {
      // Get payment information
      const firstPaymentRow = cancelPaymentRows.find(
        (row) => parseFloat(row.amount || 0) > 0
      );
      const payMethod = firstPaymentRow?.pay_mode || "";
      const payAmount = parseFloat(firstPaymentRow?.amount || 0);
      const bankAccountId = firstPaymentRow
        ? resolveRowBankAccountId(firstPaymentRow, bankAccountsList)
        : null;

      if (payAmount > 0 && !bankAccountId) {
        toast.error("Please select a valid cash/bank account for refund");
        return;
      }

      const pageSource =
        orderType === "repairing" ? "repairing_order" : "custom_order";
      const orderId = orderData.id;
      const hasSelectedItems =
        !isWholeOrder &&
        Array.isArray(selectedItems) &&
        selectedItems.length > 0;

      const selectedItemIds = hasSelectedItems
        ? selectedItems
          .map((item) => item?.id)
          .filter((id) => id !== null && id !== undefined)
        : [];

      const selectedTagNos = hasSelectedItems
        ? selectedItems
          .map((item) => item?.tag_no)
          .filter((tag) => tag !== null && tag !== undefined && tag !== "")
        : [];

      // Prepare payload based on cancellation type
      const payload = {
        page_source: pageSource,
        order_id: orderId,
        reason: cancelReason.trim(),
        date: cancelDate,
        pay_method: payMethod,
        pay_amount: payAmount,
        remarks: cancelRemarks || "",
        by_user: byUser,
      };

      if (selectedItemIds.length > 0) {
        payload.item_ids = selectedItemIds;
      }

      if (selectedTagNos.length > 0) {
        payload.tag_no = selectedTagNos.join(",");
      }

      if (isWholeOrder) {
        payload.is_whole_order = true;
        payload.cancel_order_entries = true;

        const orderEntries =
          orderType === "repairing"
            ? orderData?.repairing_order_entry
            : orderData?.custom_order_entry;
        if (Array.isArray(orderEntries)) {
          const existingTagNos = orderEntries
            .map((item) => item?.tag_no)
            .filter((tag) => tag);
          if (existingTagNos.length > 0) {
            payload.tag_no = existingTagNos.join(",");
          }
        }
      }

      if (payAmount > 0 && bankAccountId) {
        payload.bank_account_id = bankAccountId;
        payload.bank_type = bankAccountId;
      }

      const needsRefundIdempotency = payAmount > 0;
      const idempotencyKey = needsRefundIdempotency
        ? generateIdempotencyKey()
        : undefined;
      if (idempotencyKey) {
        payload.idempotency_key = idempotencyKey;
      }

      const cancelData = await postCancelOrder(payload, { idempotencyKey });

      if (isErpApiSuccess(cancelData) || cancelData?.message === "radha") {
        toast.success(
          isWholeOrder
            ? "Order cancelled successfully"
            : "Selected items cancelled successfully",
        );
        setCancelReason("");
        setCancelRemarks("");
        onHide();
        if (onSuccess) onSuccess();
      } else {
        toast.error(
          cancelData?.message || "Failed to cancel order. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error(
        getErpRequestErrorMessage(
          error,
          "Failed to cancel order. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCancelReason("");
    setCancelRemarks("");
    setCancelStep(1);
    onHide();
  };

  if (!orderData) return null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="d-flex align-items-center">
              <span className="fw-semibold">
                {isWholeOrder ? "Cancel Order" : "Cancel Selected Items"}
              </span>
              {orderData && (
                <span className="ms-2 text-muted small">
                  |{" "}
                  {orderData.co_number ||
                    orderData.token_no ||
                    orderData.id ||
                    ""}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* Step indicator */}
              {(() => {
                const total = 2;
                const steps = Array.from({ length: total }, (_, i) => i + 1);
                return (
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    {steps.map((s, idx) => (
                      <div key={s} className="d-flex align-items-center">
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center ${cancelStep >= s ? "text-white" : "bg-light text-muted"
                            } ${cancelStep === s
                              ? "border border-2 border-white"
                              : ""
                            }`}
                          style={{
                            width: 26,
                            height: 26,
                            fontSize: 12,
                            ...(cancelStep >= s
                              ? { backgroundColor: "var(--theme-primary)" }
                              : {}),
                          }}
                          title={`Step ${s} of ${total}`}
                        >
                          {s}
                        </div>
                        {idx < steps.length - 1 && (
                          <div
                            style={{
                              width: 80,
                              height: 2,
                              backgroundColor:
                                cancelStep > s
                                  ? "var(--theme-primary)"
                                  : undefined,
                            }}
                            className={cancelStep > s ? "" : "bg-light"}
                          ></div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Common header info */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-dark">
              <strong>Order No:</strong>{" "}
              {orderData.co_number ||
                orderData.token_no ||
                orderData.id ||
                "N/A"}
            </div>
            <div className="d-flex align-items-center gap-2 text-muted small">
              <strong className="text-dark">Date:</strong>
              <input
                type="date"
                className={`form-control form-control-sm d-inline-block ${buttonClass}`}
                style={{ width: "auto", display: "inline-block" }}
                value={cancelDate}
                onChange={(e) => setCancelDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Step 1: Cancellation Type and Reason */}
        {cancelStep === 1 && (
          <div>
            {selectedItems.length > 0 && (
              <div className="mb-4">
                <strong className="mb-2 d-block">Selected Items:</strong>
                <div className="mt-2">
                  {selectedItems.map((item, idx) => (
                    <div key={idx} className="mb-1">
                      <span className="fw-bold text-dark">
                        {item.tag_no
                          ? `${item.tag_no} :`
                          : item.product_group_name || `Item ${idx + 1}`}
                      </span>
                      {item.product_group_name && (
                        <span className="text-muted ms-2 fw-semibold">
                          {item.product_group_name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Reason for Cancellation </strong>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Please provide a reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                required
                className={buttonClass}
              />
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="primary"
                className={buttonClass}
                onClick={() => {
                  if (!cancelReason.trim()) {
                    toast.error("Please provide a reason for cancellation");
                    return;
                  }
                  setCancelStep(2);
                }}
                disabled={!cancelReason.trim()}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Payment Settlement */}
        {cancelStep === 2 && (
          <div>
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="table-responsive border rounded-0">
                  <table className={`table table-bordered mb-0 ${tableClass}`}>
                    <thead>
                      <tr className="bg-light">
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "50px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">No.</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "120px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Mode</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "120px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Type</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "150px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Remarks</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "100px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Amt</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelPaymentRows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="p-0 align-middle text-center">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <span className="small">{index + 1}</span>
                            </div>
                          </td>
                          <td className="p-0 align-middle">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <Select
                                options={PAYMENT_METHODS}
                                value={PAYMENT_METHODS.find(
                                  (method) => method.value === row.pay_mode
                                )}
                                onChange={(selectedOption) =>
                                  handleCancelPaymentRowChange(
                                    row.id,
                                    "pay_mode",
                                    selectedOption?.value || ""
                                  )
                                }
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    border: "none",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                    minHeight: "38px",
                                    height: "38px",
                                  }),
                                  valueContainer: (provided) => ({
                                    ...provided,
                                    padding: "0 8px",
                                    height: "38px",
                                  }),
                                  input: (provided) => ({
                                    ...provided,
                                    margin: "0",
                                    padding: "0",
                                  }),
                                  indicatorSeparator: () => ({
                                    display: "none",
                                  }),
                                  dropdownIndicator: (provided) => ({
                                    ...provided,
                                    padding: "0 4px",
                                  }),
                                }}
                                isSearchable={false}
                                placeholder="Select Mode"
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                          </td>
                          <td className="p-0 align-middle">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              {(() => {
                                // Create common select styles
                                const selectStyles = {
                                  control: (provided) => ({
                                    ...provided,
                                    border: "none",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                    minHeight: "38px",
                                    height: "38px",
                                  }),
                                  valueContainer: (provided) => ({
                                    ...provided,
                                    padding: "0 8px",
                                    height: "38px",
                                  }),
                                  input: (provided) => ({
                                    ...provided,
                                    margin: "0",
                                    padding: "0",
                                  }),
                                  indicatorSeparator: () => ({
                                    display: "none",
                                  }),
                                  dropdownIndicator: (provided) => ({
                                    ...provided,
                                    padding: "0 4px",
                                  }),
                                };

                                const bankOptions = (bankAccountsList || []).map(
                                  (bank) => ({
                                    value: String(bank.id),
                                    label:
                                      bank.bank_name ||
                                      bank.account_number ||
                                      `Bank ${bank.id}`,
                                  }),
                                );

                                const options = [
                                  { value: "", label: "Select Type" },
                                  ...bankOptions,
                                ];

                                return (
                                  <Select
                                    options={options}
                                    value={options.find(
                                      (option) =>
                                        option.value !== "" &&
                                        option.value === String(row.type || ""),
                                    )}
                                    onChange={(selectedOption) =>
                                      handleCancelPaymentRowChange(
                                        row.id,
                                        "type",
                                        selectedOption?.value || ""
                                      )
                                    }
                                    styles={selectStyles}
                                    isSearchable={true}
                                    placeholder="Select Type"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    noOptionsMessage={() =>
                                      "No options available"
                                    }
                                  />
                                );
                              })()}
                            </div>
                          </td>
                          <td className="p-0 align-middle">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <input
                                type="text"
                                className={`form-control form-control-sm border-0 ${buttonClass}`}
                                value={row.remarks}
                                onChange={(e) =>
                                  handleCancelPaymentRowChange(
                                    row.id,
                                    "remarks",
                                    e.target.value
                                  )
                                }
                                placeholder="Remarks"
                                style={{ fontSize: "0.85rem", width: "100%" }}
                              />
                            </div>
                          </td>
                          <td className="p-0 align-middle text-center">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className={`form-control form-control-sm border-0 text-end ${buttonClass}`}
                                value={row.amount}
                                onChange={(e) =>
                                  handleCancelPaymentRowChange(
                                    row.id,
                                    "amount",
                                    e.target.value
                                  )
                                }
                                placeholder="0.00"
                                style={{ fontSize: "0.85rem", width: "100%" }}
                                onWheel={(e) => e.target.blur()}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-6">
                <div className={`card border ${cardClass}`}>
                  <div className="card-body p-3">
                    <div className="row g-3">
                      <div className="col-12 border-bottom pb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-muted">Total Amount:</span>
                          <span className="fw-bold">
                            {parseFloat(
                              orderData.final_amount ||
                              orderData.total_amount ||
                              0
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Paid Amount:</span>
                          <span className="fw-bold text-success">
                            {parseFloat(
                              orderData.rec_amount ||
                              orderData.rec_amt ||
                              orderData.advance ||
                              (Array.isArray(orderData.payments)
                                ? orderData.payments.reduce(
                                  (sum, p) =>
                                    sum +
                                    parseFloat(p.amount || p.total_amount || 0),
                                  0
                                )
                                : 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="col-12">
                        <label
                          className="form-label mb-1 fw-semibold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Remarks:
                        </label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${buttonClass}`}
                          value={cancelRemarks}
                          onChange={(e) => setCancelRemarks(e.target.value)}
                          placeholder="Enter remarks..."
                          style={{
                            fontSize: "0.9rem",
                            padding: "0.375rem 0.5rem",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                className={buttonClass}
                onClick={() => setCancelStep(1)}
              >
                Back
              </Button>
              <Button
                variant="primary"
                className={buttonClass}
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Cancelling..." : "Confirm Cancel"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default CancelOrderModal;
